"use client";

import * as React from "react";
import { Heart, Star, Shield, Droplets, Backpack } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "@/components/common/Link";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 6,
  product_cat: [17]
};
const wp_query = new WP_Query(wp_query_params);
const tripTabs = [{
  value: "beach",
  label: "Beach Escape"
}, {
  value: "cruise",
  label: "Luxury Cruise"
}, {
  value: "adventure",
  label: "Island Adventure"
}, {
  value: "family",
  label: "Family Resort"
}, {
  value: "allinclusive",
  label: "All-Inclusive"
}];
const volumes = ["3 Nights", "5 Nights", "7 Nights", "10 Nights", "14 Nights", "21 Nights"];
const carryLoads = ["Relaxing", "Active", "Luxury", "Budget", "Family", "Romantic"];
const weights = ["Tropical", "Coastal", "Mountain", "Island", "Resort", "Private"];
const ratings = [4.8, 4.9, 4.7, 4.6, 5.0, 4.8];
const reviewCounts = [128, 96, 214, 67, 189, 152];
function ProductsGrid() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-[4/5] rounded-lg bg-muted/40 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground">
        Unable to load travel packages at the moment.
      </div>;
  }
  if (products.length === 0) {
    return <div className="text-sm text-muted-foreground">
        No travel packages available right now.
      </div>;
  }
  return <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3">
      {products.map((product, i) => {
      const volume = volumes[i % volumes.length];
      const carry = carryLoads[i % carryLoads.length];
      const weight = weights[i % weights.length];
      const rating = ratings[i % ratings.length];
      const reviews = reviewCounts[i % reviewCounts.length];
      return <article data-index={i} key={product.id} className="group flex flex-col">
            <div className="relative aspect-[4/5] overflow-hidden bg-secondary/40 rounded-lg shadow-md">
              <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: "linear-gradient(to right, var(--border) 1px, transparent 1px), linear-gradient(to bottom, var(--border) 1px, transparent 1px)",
            backgroundSize: "32px 32px"
          }} aria-hidden="true" />
              <Link to={product.link} className="absolute inset-0 block">
                {product.featured_image_url && <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
              </Link>

              <Badge variant="secondary" className="absolute top-4 left-4 rounded-lg bg-background/95 text-foreground border-border px-3 py-1 font-default text-xs tracking-wider">
                {volume}
              </Badge>

              <Button variant="ghost" size="icon" aria-label="Add to wishlist" className="absolute top-3 right-3 size-9 rounded-lg bg-background/90 hover:bg-background text-foreground">
                <Heart className="size-4" />
              </Button>
            </div>

            <div className="mt-5 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <span className="font-default text-[10px] uppercase tracking-[0.2em] text-primary font-bold">
                  {weight}
                </span>
                <div className="flex items-center gap-1.5">
                  <Star className="size-3.5 fill-secondary text-secondary" />
                  <span className="font-default text-xs text-foreground">
                    {rating.toFixed(1)}
                  </span>
                  <span className="font-default text-xs text-muted-foreground">
                    ({reviews})
                  </span>
                </div>
              </div>

              <Link to={product.link}>
                <h3 className="font-default text-2xl font-semibold text-foreground leading-tight tracking-tight hover:text-primary transition-colors">
                  {product.title}
                </h3>
              </Link>

              <div className="font-default text-[11px] uppercase tracking-wider text-muted-foreground border-t border-border pt-3">
                Style: {carry} <span className="mx-2 opacity-50">•</span>{" "}
                Vibe: Relaxed{" "}
                <span className="mx-2 opacity-50">•</span> Region: Caribbean
              </div>

              <div className="flex items-center gap-4 text-muted-foreground">
                <div className="flex items-center gap-1.5" title="Beach access">
                  <Backpack className="size-4" strokeWidth={1.5} />
                </div>
                <div className="flex items-center gap-1.5" title="Insurance included">
                  <Shield className="size-4" strokeWidth={1.5} />
                </div>
                <div className="flex items-center gap-1.5" title="All-inclusive">
                  <Droplets className="size-4" strokeWidth={1.5} />
                </div>
              </div>

              <div className="flex items-end justify-between gap-4 pt-2">
                <div>
                  <span className="font-default text-2xl text-primary font-bold">
                    R{product.price}
                  </span>
                </div>
                <AddToCartButton productId={product.id} variant="default" size="sm" buttonClassName="bg-primary text-white rounded-lg" text="Book Now" showQuantity={false} />
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function CaribbeanDestinations() {
  return <section data-section-id="368" id="caribbeandestinationstab" className="bg-background text-foreground py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16 mb-16">
          <div>
            <span className="font-default text-xs uppercase tracking-[0.3em] text-secondary font-bold">
              Explore Paradise
            </span>
            <h2 className="mt-5 font-default text-5xl md:text-6xl font-bold text-foreground leading-[1.05] tracking-tight">
              Caribbean Getaways
            </h2>
          </div>
          <div className="flex lg:justify-end lg:items-end">
            <p className="max-w-md text-base leading-relaxed text-muted-foreground font-default">
              Discover our hand-picked Caribbean travel packages. From pristine white-sand beaches to luxury island resorts, find the perfect escape tailored to your dream vacation.
            </p>
          </div>
        </div>

        <div className="mb-12 border-b border-border">
          <Tabs defaultValue="beach" className="w-full">
            <TabsList className="bg-transparent h-auto p-0 flex flex-wrap gap-2 sm:gap-8 justify-start">
              {tripTabs.map((tab, i) => <TabsTrigger data-index={i} key={tab.value} value={tab.value} className="border-0 border-b-2 border-transparent bg-transparent px-1 pb-4 pt-0 font-default text-xs uppercase tracking-[0.2em] text-muted-foreground data-[state=active]:bg-transparent data-[state=active]:border-secondary data-[state=active]:text-primary data-[state=active]:shadow-none transition-colors hover:text-primary">
                  {tab.label}
                </TabsTrigger>)}
            </TabsList>
          </Tabs>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 6,
        product_cat: [17]
      }}>
          <ProductsGrid />
        </EcommerceProductsProvider>
      </div>
    </section>;
}